import {
  batchCreateRecord,
  batchDeleteElementAndSubs,
  batchMovePersonToOtherRecord,
  getNestedOrFloatingElementById,
  getNestedSubelementsByType,
  getNextArkId,
  getPersonName,
  getPersonRelationships,
} from '@fs/zion-sls'

/**
 * @typedef {Object} BreakingRelationship
 * @property {string} id - Relationship element id, falling back to the counterpart's person id.
 * @property {string} personName - The counterpart's full name.
 * @property {string} relationshipLabel - Localized label, from the viewed person's side.
 */

/**
 * List the relationships a move out of the group would break.
 *
 * All of them, not just counterparts staying behind — the move deletes every one.
 *
 * @param {Object} params
 * @param {Object} params.person - The person being moved out.
 * @param {Object} params.record - The group they are leaving.
 * @param {Array<Object>} params.images - SLS images.
 * @returns {Array<BreakingRelationship>} Relationships the move will remove.
 */
export const getBreakingRelationships = ({ person, record, images }) => {
  if (!person || !record || !images) return []

  return (getPersonRelationships(person, images, false) || [])
    .filter((rel) => rel?.otherPersonId)
    .map((rel) => {
      const otherPerson = getNestedOrFloatingElementById(images, rel.otherPersonId)
      return {
        id: rel.id || rel.otherPersonId,
        personName: otherPerson ? getPersonName({ person: otherPerson }) : '',
        relationshipLabel: rel.label,
      }
    })
}

/**
 * Build the SLS dispatches moving a person into a new group of their own.
 *
 * The group left behind is deleted if empty, or has its last member set primary. Auto-primary
 * applies only to groups this flow creates or reduces; a pre-existing one-person group keeps its
 * no-primary-person alert.
 *
 * @param {Object} params
 * @param {Object} params.person - The person being moved out.
 * @param {Object} params.record - The group they are leaving.
 * @param {string} params.currentPartitionKey - Current image partition key.
 * @returns {Promise<Array<Object>>} SLS dispatches to apply.
 */
export const getMoveOutOfGroupDispatches = async ({ person, record, currentPartitionKey }) => {
  if (!person) return []

  const dispatches = []

  const newRecordId = await getNextArkId('record')
  dispatches.push(...batchCreateRecord({ imagePartitionKey: currentPartitionKey, recordArk: newRecordId }))

  // Moving the person also removes their relationships; no counterpart comes along.
  dispatches.push(
    ...batchMovePersonToOtherRecord({
      person,
      destRecordId: newRecordId,
      sourceRecPartitionKey: record.partitionKey,
      destRecPartitionKey: currentPartitionKey,
    })
  )

  // A group of one always has a primary person.
  if (!person.primary) {
    dispatches.push({
      type: 'SET_PRIMARY',
      value: { partitionKey: currentPartitionKey, id: person.id, primary: true },
    })
  }

  const remainingMembers = (getNestedSubelementsByType(record, 'PERSON') || []).filter(
    (member) => member?.id !== person.id
  )

  if (remainingMembers.length === 0) {
    dispatches.push(...(batchDeleteElementAndSubs({ element: record }) || []))
  } else if (remainingMembers.length === 1) {
    const [lastMember] = remainingMembers
    if (!lastMember.primary) {
      dispatches.push({
        type: 'SET_PRIMARY',
        value: { partitionKey: lastMember.partitionKey || currentPartitionKey, id: lastMember.id, primary: true },
      })
    }
  }

  return dispatches
}

/**
 * Form values with a moved-out member gone. The row and the relationship id it was loaded with go
 * together: the next save resolves every id in `relationshipsById` against SLS, and the move already
 * deleted that one. Applied via `reset` so the commit also becomes the new clean baseline.
 *
 * @param {Object} params
 * @param {Object} params.formValues - Current form values.
 * @param {string} params.movedPersonId - The member leaving the group.
 * @returns {Object} The next form values.
 */
export const getFormValuesAfterMoveOut = ({ formValues, movedPersonId }) => ({
  ...formValues,
  relationships: (formValues?.relationships ?? []).filter((rel) => rel?.personId !== movedPersonId),
  relationshipsById: Object.fromEntries(
    Object.entries(formValues?.relationshipsById ?? {}).filter(([, rel]) => rel?.otherPersonId !== movedPersonId)
  ),
})
